import {
  DocumentData,
  Timestamp,
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
} from 'firebase/firestore';

import { db } from '../config/firebase';
import { MusicFormData, MusicSuggestion } from '../types/music';

const COLLECTION_NAME = 'music_suggestions';

// Development mode check - only true if explicitly in dev mode AND no proper Firebase config
const isDevelopment =
  import.meta.env.DEV &&
  (!import.meta.env.VITE_FIREBASE_PROJECT_ID ||
    import.meta.env.VITE_FIREBASE_PROJECT_ID === 'demo-project') &&
  import.meta.env.VITE_FIREBASE_API_KEY === undefined;

export class MusicService {
  static async submitSuggestion(formData: MusicFormData): Promise<string> {
    try {
      // In development mode without Firebase, simulate success
      if (isDevelopment) {
        console.log('Development mode: Music suggestion submitted:', formData);
        return 'dev-' + Date.now().toString();
      }

      // Simple structure matching your Firestore collection
      const suggestion = {
        artist: formData.artist.trim(),
        name: formData.songTitle.trim(),
        timestamp: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), suggestion);

      return docRef.id;
    } catch (error) {
      console.error('Error submitting music suggestion:', error);

      // If Firebase is not configured, provide helpful error message
      if (error instanceof Error && error.message.includes('projectId')) {
        throw new Error(
          'Firebase is not configured. Please set up your Firebase environment variables to enable music suggestions.'
        );
      }

      throw new Error('Failed to submit music suggestion. Please try again.');
    }
  }

  static async getAllSuggestions(): Promise<any[]> {
    try {
      // In development mode without Firebase, return empty array
      if (isDevelopment) {
        console.log('Development mode: Returning empty suggestions list');
        return [];
      }

      const q = query(collection(db, COLLECTION_NAME), orderBy('timestamp', 'desc'));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => {
        const data = doc.data() as DocumentData;
        return {
          id: doc.id,
          artist: data.artist,
          songTitle: data.name, // Map 'name' field to 'songTitle' for compatibility
          timestamp: data.timestamp?.toDate() || new Date(),
        };
      });
    } catch (error) {
      console.error('Error fetching music suggestions:', error);
      throw new Error('Failed to load music suggestions.');
    }
  }
}
